import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, ShieldCheck, Mail, MessageSquare, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SiteNav } from "@/components/SiteNav";
import { OtpField } from "@/components/OtpField";
import { IdCheckStep } from "@/components/IdCheckStep";
import { api, formatError, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ADDR = [
  { id: "name", label: "Your full name (as on the letter)" },
  { id: "email", label: "Your email address (the one this letter was sent to)", type: "email" },
  { id: "line1", label: "Address line 1" },
  { id: "line2", label: "Address line 2 (optional)", optional: true },
  { id: "line3", label: "Address line 3 (optional)", optional: true },
  { id: "city", label: "Town / City" },
  { id: "postcode", label: "Postcode" },
];

const WIDE = ["name", "email", "line1", "line2", "line3"];

const IdentityForm = ({ token, info, onSent }) => {
  const [form, setForm] = useState({ name: "", email: "", line1: "", line2: "", line3: "", city: "", postcode: "", channel: "email" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/claim/${token}/identity`, form);
      onSent(data);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setBusy(false);
    }
  };
  const channelBtn = (c, Icon, label) => (
    <button type="button" data-testid={`claim-channel-${c}`} onClick={() => setForm({ ...form, channel: c })}
      className={`flex-1 flex items-center gap-2 justify-center h-11 rounded-lg border text-sm transition-colors ${form.channel === c ? "border-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300" : "border-slate-200 dark:border-slate-800"}`}>
      <Icon size={15} /> {label}
    </button>
  );
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="claim-identity-form">
      <div className="grid sm:grid-cols-2 gap-4">
        {ADDR.map((f) => (
          <div key={f.id} className={`space-y-2 ${WIDE.includes(f.id) ? "sm:col-span-2" : ""}`}>
            <Label htmlFor={`c-${f.id}`}>{f.label}</Label>
            <Input id={`c-${f.id}`} data-testid={`claim-${f.id}-input`} type={f.type || "text"} autoComplete="off" required={!f.optional} value={form[f.id]} onChange={(e) => setForm({ ...form, [f.id]: e.target.value })} className="h-11" />
          </div>
        ))}
      </div>
      <div className="space-y-2">
        <Label>Where should we send your one-time code?</Label>
        <p className="text-xs text-slate-500">Tap one option below. These are the contact details the sender gave us (partly hidden for privacy).</p>
        <div className="flex gap-3">
          {channelBtn("email", Mail, `Email ${info.recipient_email_masked}`)}
          {info.recipient_phone_masked && channelBtn("sms", MessageSquare, `Text ${info.recipient_phone_masked}`)}
        </div>
      </div>
      {error && <p data-testid="claim-identity-error" className="text-sm text-red-600">{error}</p>}
      <Button data-testid="claim-identity-submit" type="submit" disabled={busy} className="w-full h-11 rounded-full">{busy && <Loader2 className="animate-spin mr-2" size={16} />}Confirm my details</Button>
    </form>
  );
};

const ResendCode = ({ token, channel, hasPhone, onSent }) => {
  const [wait, setWait] = useState(60);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    if (wait <= 0) { return undefined; }
    const t = setTimeout(() => setWait(wait - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);
  const resend = async (to) => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.post(`/claim/${token}/resend`, to ? { channel: to } : {});
      onSent(data);
      setMsg(data.channel === "sms" ? "A new code is on its way by text." : "A new code is on its way by email.");
      setWait(60);
    } catch (e) {
      setMsg(formatError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="text-center space-y-1">
      <button type="button" data-testid="claim-resend-code-btn" onClick={() => resend()} disabled={busy || wait > 0} className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-400 hover:underline disabled:opacity-50 disabled:no-underline">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} {wait > 0 ? `Send a new code in ${wait}s` : "Send a new code"}</button>
      {hasPhone && (
        <button type="button" data-testid="claim-switch-channel-btn" onClick={() => resend(channel === "sms" ? "email" : "sms")} disabled={busy}
          className="flex mx-auto items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:underline disabled:opacity-50">
          {channel === "sms" ? <Mail size={14} /> : <MessageSquare size={14} />} {channel === "sms" ? "Email me a code instead" : "Text me a code instead"}</button>
      )}
      <p className="text-xs text-slate-500">Didn't get it? Check your spam or junk folder first.</p>
      {msg && <p data-testid="claim-resend-msg" className="text-xs text-slate-600 dark:text-slate-300">{msg}</p>}
    </div>
  );
};

const OtpStep = ({ token, sent: first, hasPhone, onDone }) => {
  const [sent, setSent] = useState(first);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/claim/${token}/confirm`, { code });
      onDone(data);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="claim-otp-form">
      <p className="text-sm text-emerald-700 dark:text-emerald-400 flex items-center gap-2"><CheckCircle2 size={16} /> Details matched. Code sent to {sent.destination}.</p>
      <OtpField value={code} onChange={setCode} testId="claim-otp-input" />
      {error && <p data-testid="claim-otp-error" className="text-sm text-red-600">{error}</p>}
      <Button data-testid="claim-otp-submit" type="submit" disabled={busy || code.length !== 6} className="w-full h-11 rounded-full">{busy && <Loader2 className="animate-spin mr-2" size={16} />}Release my letter</Button>
      <ResendCode token={token} channel={sent.channel} hasPhone={hasPhone} onSent={setSent} />
    </form>
  );
};

const Body = ({ token, info, user }) => {
  const [sent, setSent] = useState(null);
  const [mailId, setMailId] = useState(info.mail_id);
  const returning = new URLSearchParams(window.location.search).get("id_return") === "1";
  const [idStage, setIdStage] = useState(info.id_stage || returning);
  const otpDone = (data) => { if (data.status === "id_check_required") setIdStage(true); else setMailId(data.mail_id); };
  if (mailId && (info.status === "claimed" || !info.mail_id)) {
    return (
      <div data-testid="claim-success" className="text-center py-6">
        <CheckCircle2 size={40} className="mx-auto text-emerald-600" />
        <p className="font-display text-xl font-semibold mt-4">Letter released to your mailbox</p>
        <Link to={`/app/mail/${mailId}`} data-testid="claim-open-letter-link" className="inline-flex mt-6 px-6 py-3 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 font-semibold hover:bg-emerald-600 transition-colors">Open letter</Link>
      </div>
    );
  }
  if (info.status !== "pending_claim") {
    return <p data-testid="claim-unavailable" className="flex gap-2 text-sm text-red-600"><AlertTriangle size={16} /> This letter is no longer available to claim (status: {info.status.replace("_", " ")}).</p>;
  }
  if (info.locked) return <p data-testid="claim-locked" className="text-sm text-red-600">Too many failed attempts. This claim is locked — please contact the sender.</p>;
  if (!user) {
    const next = encodeURIComponent(`/claim/${token}`);
    return (
      <div className="space-y-4" data-testid="claim-auth-required">
        <p className="text-sm text-slate-600 dark:text-slate-300">To keep your letter secure, sign in or create a free PostalCloud account first.</p>
        <div className="flex flex-wrap gap-3">
          <Link to={`/register?next=${next}`} data-testid="claim-register-btn" className="px-5 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 transition-colors">Create account</Link>
          <Link to={`/login?next=${next}`} data-testid="claim-login-btn" className="px-5 py-2.5 rounded-full border border-slate-300 dark:border-slate-700 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">Sign in</Link>
        </div>
      </div>
    );
  }
  if (idStage) return <IdCheckStep token={token} returning={returning} onReleased={setMailId} />;
  return sent ? <OtpStep token={token} sent={sent} hasPhone={!!info.recipient_phone_masked} onDone={otpDone} /> : <IdentityForm token={token} info={info} onSent={setSent} />;
};

export default function Claim() {
  const { token } = useParams();
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get(`/claim/${token}`).then((r) => setInfo(r.data)).catch((e) => setError(formatError(e)));
  }, [token]);

  return (
    <div className="min-h-screen">
      <SiteNav />
      <div className="max-w-5xl mx-auto px-6 py-14 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          {info?.brand && (
            <div data-testid="claim-brand" className="mb-8 pt-4" style={{ borderTop: `4px solid ${info.brand.color}` }}>
              {info.brand.logo_url
                ? <img data-testid="claim-brand-logo" src={info.brand.logo_url} alt={info.brand.display_name} className="h-12 max-w-[260px] object-contain" />
                : <p data-testid="claim-brand-name" className="font-display text-xl font-bold" style={{ color: info.brand.color }}>{info.brand.display_name}</p>}
              <p className="text-[11px] uppercase tracking-wider text-emerald-600 mt-2 flex items-center gap-1" data-testid="claim-verified-mark"><ShieldCheck size={12} /> Verified by PostalCloud</p>
            </div>
          )}
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Verified claim</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">You have a verified letter.</h1>
          {info && (
            <dl className="mt-8 space-y-4 text-sm" data-testid="claim-info">
              <div><dt className="text-slate-500 text-xs uppercase tracking-wider">From</dt><dd data-testid="claim-sender" className="font-semibold mt-1">{info.sender_name}</dd></div>
              <div><dt className="text-slate-500 text-xs uppercase tracking-wider">Reference</dt><dd className="font-mono mt-1">{info.reference}</dd></div>
              <div><dt className="text-slate-500 text-xs uppercase tracking-wider">Sent</dt><dd className="font-mono mt-1">{fmtDate(info.sent_at)}</dd></div>
              <div><dt className="text-slate-500 text-xs uppercase tracking-wider">Held until</dt><dd className="font-mono mt-1">{fmtDate(info.expires_at)}</dd></div>
            </dl>
          )}
          <p className="mt-8 text-xs text-slate-500 flex gap-2"><ShieldCheck size={14} className="text-emerald-600 shrink-0" /> Your details are compared with what the sender provided. Every attempt is recorded in the audit trail.</p>
        </div>
        <div className="lg:col-span-3 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 sm:p-8">
          {error && <p data-testid="claim-error" className="text-sm text-red-600">{error}</p>}
          {!info && !error && <Loader2 className="animate-spin text-emerald-600" />}
          {info && user !== null && <Body token={token} info={info} user={user} />}
        </div>
      </div>
    </div>
  );
}
